import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { truckingReportExtAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function PSACCBillingStripReportPage() {
  const [dateFrom,   setDateFrom]   = useState(startOfMonth());
  const [dateTo,     setDateTo]     = useState(today());
  const [soaNumber,  setSoaNumber]  = useState('');
  // The "Additional Stripping Fee" statement number — derived from the matched
  // bill as bill.SOANumber + 1. Only assigned when Print is clicked for the
  // first time on a given bill (see handlePrint) — never on page load. Still
  // freely editable; a manual edit is respected as-is and skips auto-reservation.
  const [printSoaNumber, setPrintSoaNumber] = useState('');
  const [soaNumberTouched, setSoaNumberTouched] = useState(false);
  // Set when the derived number (bill.SOANumber + 1) is already held by another
  // bill or another bill's stripping statement. Shown as a warning after load
  // and enforced as a hard stop on Print.
  const [soaConflict, setSoaConflict] = useState('');
  // The single Trucking bill this report matched — the additional-stripping
  // statement number can only be auto-assigned when exactly one bill is in
  // range (no single bill = no single "+1" to derive).
  const [billId,     setBillId]     = useState(null);
  const [groups,     setGroups]     = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [storeInfo,  setStoreInfo]  = useState({});
  const [loading,    setLoading]    = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  const [error,      setError]      = useState('');

  const hasRows = groups.some((g) => g.rows.length > 0);

  const load = async () => {
    setLoading(true); setError('');
    setPrintSoaNumber(''); setSoaNumberTouched(false); setBillId(null); setSoaConflict('');
    try {
      const { data } = await truckingReportExtAPI.getPSACCBillingStrip({ dateFrom, dateTo, soaNumber });
      const loadedGroups = data.groups || [];
      setGroups(loadedGroups);
      setSummary(data.summary || {});
      setStoreInfo(data.storeInfo || {});
      if (loadedGroups.length === 1) {
        const bid = loadedGroups[0].billId;
        setBillId(bid);
        // Show the expected number ahead of time — this is a preview only,
        // it does NOT reserve anything (that happens on Print).
        truckingReportExtAPI.previewAdditionalStrippingSOANumber(bid)
          .then((r) => {
            if (r.data?.soaNumber) setPrintSoaNumber(r.data.soaNumber);
            if (r.data?.collision) {
              setSoaConflict(
                `SOA Number ${r.data.collision.soaNumber} is already in use. Printing will be blocked until the conflict is resolved.`
              );
            }
          })
          .catch((e) => {
            setSoaConflict(e?.response?.data?.message || '');
          });
      }
      setLoaded(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Print opens a dedicated, standalone print route (same pattern as PSACC
  // Billing / Revenue Per Van) instead of window.print() on this page — this
  // page is wrapped in Layout and uses card-style utility classes (rounded-lg,
  // shadow-md, bg-gray-50) that a global print stylesheet hides, which was
  // silently blanking the report when printed in place.
  const handlePrint = async () => {
    // Printing IS issuing the additional-stripping statement — reserve its SOA
    // Number the FIRST time this bill is printed, unless the user manually
    // typed a value. Once reserved, soaNumberTouched locks it in so reprinting
    // the same bill reuses that number instead of reserving a new one.
    let soaToUse = printSoaNumber;
    if (!soaNumberTouched && billId) {
      try {
        const r = await truckingReportExtAPI.getAdditionalStrippingSOANumber(billId);
        soaToUse = r.data?.soaNumber || printSoaNumber;
        setPrintSoaNumber(soaToUse);
        setSoaNumberTouched(true);
        setSoaConflict('');
      } catch (e) {
        // A failed reservation used to fall through and print anyway. It can no
        // longer: the number is derived from the bill, so the failure case that
        // matters is "this number already belongs to something else" — printing
        // it would put a duplicate SOA Number on a carrier-facing document.
        setSoaConflict(
          e?.response?.data?.message || 'Could not reserve the statement number. Nothing was printed.'
        );
        setError('');
        return;
      }
    }
    // soaNumber here is the actual search filter (matches billing records —
    // must match what this report page is showing). statementNo is the
    // separate, freshly-reserved additional-stripping label — it must NOT be
    // used to filter data, since it doesn't match any real bill's SOANumber.
    const params = new URLSearchParams({
      dateFrom, dateTo,
      ...(soaNumber.trim() ? { soaNumber: soaNumber.trim() } : {}),
      ...(soaToUse ? { statementNo: soaToUse } : {}),
    });
    window.open(`/billing/reports/print/psacc-strip?${params}`, '_blank');
  };

  return (
    <Layout pageTitle="PSACC Billing (Stripping)">
      {/* Filter Bar */}
      <div className="bg-white border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SOA Number</label>
          <input type="text" value={soaNumber} onChange={e => setSoaNumber(e.target.value)}
            placeholder="e.g. 2026-015"
            className="border rounded px-2 py-1.5 text-sm w-36" />
        </div>
        <button onClick={load} disabled={loading}
          className="bg-teal-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">
          {loading ? 'Loading…' : 'Load Report'}
        </button>
        {loaded && hasRows && (
          <button onClick={handlePrint}
            className="bg-gray-600 text-white px-4 py-1.5 rounded text-sm">
            Print
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {soaConflict && (
        <div className="mb-3 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {soaConflict}
        </div>
      )}

      {loaded && (
        <div className="bg-white border rounded-lg p-4">
          {/* Company Header */}
          <div className="mb-4 text-center">
            <p className="font-bold text-base">{storeInfo.companyName || 'ARVANNAH RENZ TRUCKING INC.'}</p>
            <p className="text-sm">{storeInfo.address || 'Q. ABETO ST. BRGY. ABETO-MIRASOL TAFT SOUTH MANDURIAO, ILOILO CITY'}</p>
            <p className="text-sm">{storeInfo.tin ? `TIN NO. ${storeInfo.tin}` : 'TIN NO. 768-950-988-000-VAT'}</p>
            <p className="text-sm font-medium mt-1 flex items-center justify-center gap-1">
              STATEMENT OF ACCOUNT NO.
              <input
                type="text"
                value={printSoaNumber}
                onChange={(e) => { setPrintSoaNumber(e.target.value); setSoaNumberTouched(true); }}
                placeholder="___________"
                title="Preview of the next SOA number after the bill's own — only reserved/incremented when you click Print. Editing here overrides that and only relabels this printout."
                className="border-b border-dotted border-gray-400 text-center bg-transparent focus:outline-none focus:border-gray-600 focus:border-solid w-32"
              />
            </p>
          </div>

          {/* Addressed-to + Date */}
          <div className="flex justify-between mb-4 text-sm">
            <div>
              <p className="font-medium">PHILIPPINE SPAN ASIA CARRIER CORP.</p>
              <p>Coastal Road, Brgy. Balabago, Jaro, Iloilo City</p>
            </div>
            <div className="text-right">
              <p>Date: <span className="font-medium">{fmtDate(new Date())}</span></p>
            </div>
          </div>

          {/* Grouped table — Billing group -> Vessel/Voy span sub-group -> subtotals */}
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-1">DATE</th>
                  <th className="border px-2 py-1">PLATE NO.</th>
                  <th className="border px-2 py-1">WB NO.</th>
                  <th className="border px-2 py-1">VESSEL / VOY. NO.</th>
                  <th className="border px-2 py-1">VAN NO.</th>
                  <th className="border px-2 py-1">CONSIGNEE</th>
                  <th className="border px-2 py-1">LOCATION</th>
                  <th className="border px-2 py-1 text-right">ADD. STRIPPING</th>
                  <th className="border px-2 py-1 text-right">RATES</th>
                  <th className="border px-2 py-1 text-right">TOTAL AMOUNT PER SPAN</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-6 text-gray-400 border">No stripping charges found for this range.</td></tr>
                ) : groups.map((g) => (
                  <React.Fragment key={g.billId}>
                    {/* Billing header row 
                    <tr className="bg-teal-50">
                      <td colSpan={10} className="border px-2 py-2">
                        <div className="text-right">
                          <p className="font-medium text-teal-900">STATEMENT OF ACCOUNT NO. {g.soaNumber || g.billName}</p>
                          <p className="text-teal-700">Date: {fmtDate(g.billDate)}</p>
                        </div>
                        <p className="text-[11px] text-teal-700 mt-1">
                          Billing: {g.billName} —
                          <span className={`ml-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            g.isVoided ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-700'
                          }`}>
                            {g.isVoided ? 'VOIDED' : (g.status || 'draft').toUpperCase()}
                          </span>
                          <span className="ml-2">({g.waybillCount} waybill{g.waybillCount === 1 ? '' : 's'})</span>
                        </p>
                      </td>
                    </tr>*/}

                    {(g.spans || []).map((span, si) => (
                      <React.Fragment key={si}>
                        {/* Vessel/Voy span sub-header 
                        <tr style={{ backgroundColor: '#f0fdfa' }}>
                          <td colSpan={10} className="border px-2 py-1 font-medium text-teal-800">
                            VESSEL / VOY. NO.: {span.key}
                          </td>
                        </tr>*/}
                        {span.rows.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="border px-2 py-1 whitespace-nowrap">{fmtDate(r.date)}</td>
                            <td className="border px-2 py-1">{r.plateNo}</td>
                            <td className="border px-2 py-1">{r.wbNo}</td>
                            <td className="border px-2 py-1">{r.vesselVoyage}</td>
                            <td className="border px-2 py-1">{r.vanNo}</td>
                            <td className="border px-2 py-1">{r.consignee}</td>
                            <td className="border px-2 py-1">{r.location}</td>
                            <td className="border px-2 py-1 text-right">₱{PHP(r.additionalStripping || 0)}</td>
                            <td className="border px-2 py-1 text-right"></td>
                            {/* Merged cell: TOTAL AMOUNT PER SPAN appears once
                                per span, vertically centered across its rows. */}
                            {i === 0 && (
                              <td rowSpan={span.rows.length} className="border px-2 py-1 text-center align-middle font-medium">
                                
                              </td>
                            )}
                          </tr>
                        ))}
                        {si < g.spans.length - 1 && (
                          <tr><td colSpan={10} className="border-0 py-1">&nbsp;</td></tr>
                        )}
                      </React.Fragment>
                    ))}

                   
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {summary && groups.length > 0 && (
            <p className="text-xs text-gray-500 mb-4">
              {summary.billingCount} billing{summary.billingCount === 1 ? '' : 's'} · {summary.waybillCount} waybill{summary.waybillCount === 1 ? '' : 's'} with stripping charges in range
            </p>
          )}

          {/* Financial Summary */}
          <div className="flex justify-end mb-8">
            <table className="text-sm border-collapse">
              <tbody>
                <tr>
                  <td className="pr-8 py-0.5 text-right text-gray-600">RATE:</td>
                  <td className="text-right font-medium w-36">₱{PHP(summary?.totalRate)}</td>
                </tr>
                <tr>
                  <td className="pr-8 py-0.5 text-right text-gray-600">STRIPPING:</td>
                  <td className="text-right font-medium w-36">₱{PHP(summary?.totalStripping)}</td>
                </tr>
                <tr className="border-t-2 border-gray-800">
                  <td className="pr-8 py-1 text-right font-bold">TOTAL AMOUNT DUE:</td>
                  {/* Per Excel source: TOTAL AMOUNT DUE = SUM(ADD. STRIPPING) only */}
                  <td className="text-right font-bold text-gray-900 w-36">
                    ₱{PHP(summary?.totalAmountDue)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Signature Block */}
          <div className="flex justify-between text-sm mt-12">
            <div>
              <p className="font-medium border-t border-gray-800 pt-1 pr-16">JOHN EFREN OCTAVIANO</p>
              <p className="text-gray-600 text-xs">CHIEF FINANCIAL OFFICER</p>
            </div>
            <div>
              <p>RECEIVED BY: <span className="inline-block border-b border-gray-800 w-48">&nbsp;</span></p>
              <p className="mt-4">Date: <span className="inline-block border-b border-gray-800 w-48">&nbsp;</span></p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
